import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from '@mui/material';
import CollapsibleSection from '../../../shared/components/CollapsibleSection';
import FormActionBar from '../../../shared/components/FormActionBar';
import { AWS_REGIONS } from '../../../shared/constants/aws';
import { useAccountForm, getAccountIdError, getRoleNameError } from '../hooks/useAccountForm';

interface AccountFormFieldsProps {
  form: ReturnType<typeof useAccountForm>;
  /** Admin: the break-glass decision, and the automation and onboarding role
      names, which name administrator roles Cloud2 assumes in the customer's
      account. The server takes all three from an admin only; below admin the
      switch is not shown and both role fields are shown disabled. */
  allowAccountDecisions?: boolean;
  /** Whether account_baseline is on. Off, every baseline action is refused, so
      the decision is shown to no one. */
  baselineEnabled: boolean;
}

/**
 * Registering a new account. A saved account is edited in its tabs, each the
 * one place its own fields change; this form is never shown for one.
 */
const AccountFormFields: React.FC<AccountFormFieldsProps> = ({
  form,
  allowAccountDecisions = false,
  baselineEnabled,
}) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const adminOnlyHint = allowAccountDecisions ? undefined : 'Only an admin may change this';

  const accountIdError = useMemo(
    () => getAccountIdError(form.formData.aws_account_id, form.entities),
    [form.formData.aws_account_id, form.entities],
  );
  const readRoleError = useMemo(
    () => getRoleNameError(form.formData.read_role_name),
    [form.formData.read_role_name],
  );
  const writeRoleError = useMemo(
    () => form.formData.write_role_name ? getRoleNameError(form.formData.write_role_name) : null,
    [form.formData.write_role_name],
  );
  const automationRoleError = useMemo(
    () => form.formData.automation_role_name ? getRoleNameError(form.formData.automation_role_name) : null,
    [form.formData.automation_role_name],
  );
  const onboardingRoleError = useMemo(
    () => form.formData.onboarding_role_name ? getRoleNameError(form.formData.onboarding_role_name) : null,
    [form.formData.onboarding_role_name],
  );

  return (
    // No scroll of its own: the modal's detail pane scrolls it.
    <Box sx={{ flexShrink: 0, p: 3 }}>
      <Box sx={{ maxWidth: 800 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>New AWS Account</Typography>
        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'grid', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="AWS Account ID *"
              value={form.formData.aws_account_id}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                form.handleChange('aws_account_id', value);
              }}
              placeholder="123456789012"
              inputProps={{
                maxLength: 12,
                pattern: '[0-9]{12}',
                inputMode: 'numeric'
              }}
              error={!!accountIdError}
              helperText={accountIdError || "12-digit AWS account number"}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Account Name *"
              value={form.formData.account_name}
              onChange={(e) => form.handleChange('account_name', e.target.value)}
              placeholder="Production"
              helperText="Friendly name for this account"
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/*
            Decided at registration: it decides how the access roles are built,
            and they are installed once. On a saved account it lives on the
            Baselines tab. Admins only, because saving it goes through
            account_baseline.SetAccountBreakGlass, which is admin level; and
            only while account_baseline is on, since every baseline action is
            refused while it is off.
          */}
          {baselineEnabled && allowAccountDecisions && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.formData.break_glass_required ?? false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.handleChange('break_glass_required', e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Customer controls our admin access</Typography>
                  <Typography variant="caption" color="text.secondary">
                    We stay read-only here. The customer runs break-glass in their own tenant to
                    let us in, and closes it afterwards. For customers who may not grant us
                    standing admin.
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>

        <Box sx={{ mt: 3 }}>
          <CollapsibleSection
            title="Account Details"
            expanded={detailsExpanded}
            onChange={setDetailsExpanded}
          >
            <Box sx={{ display: 'grid', gap: 3 }}>
              <TextField
                fullWidth
                label="Read-Only Role Name *"
                value={form.formData.read_role_name}
                onChange={(e) => form.handleChange('read_role_name', e.target.value)}
                placeholder="cloud2-operator-readonly"
                error={readRoleError !== null}
                helperText={readRoleError || "IAM role name for read-only access (investigations)"}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Write Role Name"
                value={form.formData.write_role_name}
                onChange={(e) => form.handleChange('write_role_name', e.target.value)}
                placeholder="cloud2-operator-admin"
                error={writeRoleError !== null}
                helperText={writeRoleError || "IAM role name for write access (remediation) - optional"}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Automation Role Name"
                value={form.formData.automation_role_name}
                onChange={(e) => form.handleChange('automation_role_name', e.target.value)}
                placeholder="cloud2-operator-automation"
                disabled={!allowAccountDecisions}
                error={automationRoleError !== null}
                helperText={
                  automationRoleError
                  || adminOnlyHint
                  || "IAM role for automation (Lambda invoke, SSM, Service Catalog) - optional"
                }
                InputLabelProps={{ shrink: true }}
              />

              {/*
                The one role in this group the customer installs. The other
                three name roles OpsPilot creates, and they are empty on a
                fresh account because they do not exist yet; this one is what
                lets OpsPilot in to create them.
              */}
              <TextField
                fullWidth
                label="Onboarding Role Name"
                value={form.formData.onboarding_role_name}
                onChange={(e) => form.handleChange('onboarding_role_name', e.target.value)}
                placeholder="cloud2-onboarding"
                disabled={!allowAccountDecisions}
                error={onboardingRoleError !== null}
                helperText={
                  onboardingRoleError
                  || adminOnlyHint
                  || 'Installed by the customer before onboarding starts. OpsPilot assumes it to create the other roles.'
                }
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="External ID"
                value={form.formData.external_id}
                onChange={(e) => form.handleChange('external_id', e.target.value)}
                placeholder="optional-external-id"
                helperText="Optional external ID for security"
                InputLabelProps={{ shrink: true }}
              />

              <FormControl fullWidth>
                <InputLabel shrink>Supported Regions *</InputLabel>
                <Select
                  multiple
                  value={form.formData.supported_regions}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    form.setError(null);
                    form.handleChange('supported_regions', value);
                    if (!value.includes(form.formData.main_region)) {
                      form.handleChange('main_region', value.length > 0 ? value[0] : '');
                    }
                  }}
                  input={<OutlinedInput label="Supported Regions *" notched />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          color={value === form.formData.main_region ? 'primary' : 'default'}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {AWS_REGIONS.map((region) => (
                    <MenuItem key={region.value} value={region.value}>
                      {region.label} {region.value === form.formData.main_region ? '(Main)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel shrink>Main Region *</InputLabel>
                <Select
                  value={form.formData.main_region}
                  onChange={(e) => {
                    form.handleChange('main_region', e.target.value);
                  }}
                  label="Main Region *"
                  notched
                  disabled={form.formData.supported_regions.length === 0}
                >
                  {form.formData.supported_regions.length === 0 ? (
                    <MenuItem value="">
                      <em>Select supported regions first</em>
                    </MenuItem>
                  ) : (
                    form.formData.supported_regions.map((regionValue) => {
                      const region = AWS_REGIONS.find(r => r.value === regionValue);
                      return (
                        <MenuItem key={regionValue} value={regionValue}>
                          {region?.label || regionValue}
                        </MenuItem>
                      );
                    })
                  )}
                </Select>
                {form.formData.supported_regions.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Select one or more supported regions first
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Event Bus Name"
                value={form.formData.event_bus_name}
                onChange={(e) => form.handleChange('event_bus_name', e.target.value)}
                placeholder="cloud2-events"
                helperText="EventBridge bus name for Service Catalog automation events"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </CollapsibleSection>
        </Box>

        <FormActionBar
          onSave={form.handleSave}
          saving={form.saving}
          saveDisabled={form.saving}
          saveLabel="Create Account"
          savingLabel="Creating..."
        />
      </Box>
    </Box>
  );
};

export default AccountFormFields;
